import {
  ID_TOKEN_EXPIRED_CODE,
  ID_TOKEN_RETRY_HEADER,
} from '@dupe/common/constants'
import { auth } from '~/src/lib/firebase/firebaseAuthInstance'

/**
 * The signed-in user's ID token as of now, not as of page load: Firebase hands
 * back its cached token until it is near expiry, then a new one. `force`
 * fetches a new one regardless. Null when nobody is signed in or Firebase
 * can't answer.
 */
export async function freshIdToken(force = false): Promise<string | null> {
  try {
    return (await auth.currentUser?.getIdToken(force)) ?? null
  } catch {
    return null
  }
}

const SEND_TOKEN_WAIT_MS = 3_000

/**
 * Resolves once Firebase has restored the saved sign-in (or found none). Right
 * after a reload `auth.currentUser` is null until then, while the page already
 * holds the stored page-load token; reading `currentUser` before this sends
 * that stale token unrefreshed (DUP-3625).
 */
async function signInRestored(): Promise<void> {
  try {
    await auth.authStateReady?.()
  } catch {
    // Firebase couldn't say; read `currentUser` as it is.
  }
}

/** `fn()`, or null if it hasn't answered within a few seconds. */
async function withinSendWait<T>(fn: () => Promise<T>): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const result = await Promise.race([
    fn(),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), SEND_TOKEN_WAIT_MS)
    }),
  ])
  clearTimeout(timer)
  return result
}

/** `freshIdToken()`, or null if it hasn't answered within a few seconds. */
async function freshIdTokenForSend(): Promise<string | null> {
  return withinSendWait(() => freshIdToken())
}

/**
 * `headers` with its `x-authorization` swapped for the token as of now.
 *
 * ⛔ A token lives an hour, and the api's chat stream answers a lapsed one
 * with a 401 (`rejectUnverifiedToken`); other routes quietly treat the sender
 * as signed out, so staff-only tools go missing with no error. Waits for
 * Firebase to restore sign-in after a reload first. Swaps only when the
 * signed-in user is the header's `x-user-id`: a token is never paired with
 * headers built for someone else, or for no one. Gives up after a few seconds in all (a stalled refresh must not hold a
 * message), sending the headers as they were.
 */
export async function withFreshIdToken(
  headers: Record<string, string>
): Promise<Record<string, string>> {
  if (!headers['x-authorization']) return headers
  const fresh = await withinSendWait(async () => {
    await signInRestored()
    // One read of the user: its token, checked against the headers, so an
    // account switch mid-call can't pair B's token with A's headers.
    const user = auth.currentUser
    if (!user || user.uid !== headers['x-user-id']) return null
    try {
      return await user.getIdToken()
    } catch {
      return null
    }
  })
  return fresh && fresh !== headers['x-authorization']
    ? { ...headers, 'x-authorization': fresh }
    : headers
}

type RetryableInit = Omit<RequestInit, 'headers' | 'body'> & {
  headers: Record<string, string>
  /** A string, so the same body can be sent again on the retry. */
  body?: string
}

/**
 * `fetch` for an api route that answers a lapsed token with 401
 * `id_token_expired` (the brain chat stream). On that 401, forces a new token
 * and retries once. When no new token for the same user can be had, or the new
 * one is refused too (the api can't verify tokens at all just now), it sends
 * without `x-authorization`, which is how the request ran before the 401
 * existed: a 401 never reaches the shopper.
 *
 * `signedOutFallback: false` returns the 401 instead of that last tokenless
 * send: for a speculative preload, a signed-out run could be cached and
 * replayed onto the owner's real turn, so no preload beats a stranger's one.
 *
 * `refreshFirst: false` sends the first request synchronously with the
 * headers as given (speculative preloads, whose callers count dispatches
 * synchronously); the 401 path still refreshes.
 */
export function fetchWithFreshIdToken(
  url: string,
  init: RetryableInit,
  {
    refreshFirst = true,
    signedOutFallback = true,
  }: { refreshFirst?: boolean; signedOutFallback?: boolean } = {}
): Promise<Response> {
  const send = (headers: Record<string, string>) =>
    fetch(url, {
      ...init,
      headers: headers['x-authorization']
        ? { ...headers, [ID_TOKEN_RETRY_HEADER]: '1' }
        : headers,
    })
  const first = refreshFirst
    ? withFreshIdToken(init.headers).then((headers) =>
        send(headers).then((res) => ({ headers, res }))
      )
    : send(init.headers).then((res) => ({ headers: init.headers, res }))
  return first.then(async ({ headers, res }) => {
    if (!(await isLapsedTokenReply(res, headers))) return res
    let last = res
    const forced = await forcedIdTokenFor(headers)
    if (forced && forced !== headers['x-authorization']) {
      const retryHeaders = { ...headers, 'x-authorization': forced }
      release(last)
      last = await send(retryHeaders)
      if (!(await isLapsedTokenReply(last, retryHeaders))) return last
    }
    // Unread: the caller gets this 401 whole.
    if (!signedOutFallback) return last
    release(last)
    // ⚠️ The api runs this as a signed-out request: on an account's chat that
    // is the stranger turn DUP-3625 describes. Reached only when no token for
    // this user can be had in time, so say so where it can be seen.
    console.warn('[auth] no verifiable sign-in token; sending signed out')
    const withoutToken = { ...headers }
    delete withoutToken['x-authorization']
    return send(withoutToken)
  })
}

/** In-flight refreshes, keyed by the token that was refused. */
const forcedRefreshes = new Map<
  string,
  Promise<{ uid: string; token: string } | null>
>()

/**
 * A newer token for the user `headers` names, or null. Waits for Firebase to
 * restore sign-in first, as a preload sent before then has not. Takes the
 * current cached token when it already differs from the one refused (another
 * request just refreshed it); mints a new one only when it doesn't.
 *
 * Shared by requests refused the same token, so a burst of 401s mints once,
 * while a request refused a different token never gets that token back.
 *
 * ⛔ The shared refresh is tagged with the user it was minted for, and a
 * request takes it only when that is the user its headers name: a browser
 * that switches accounts mid-refresh must not send one account's message
 * under the other's token.
 */
function forcedIdTokenFor(
  headers: Record<string, string>
): Promise<string | null> {
  const named = headers['x-user-id']
  if (!named) return Promise.resolve(null)
  const refused = headers['x-authorization'] ?? ''
  let pending = forcedRefreshes.get(refused)
  if (!pending) {
    pending = withinSendWait(async () => {
      await signInRestored()
      const user = auth.currentUser
      if (!user) return null
      try {
        const cached = await user.getIdToken()
        if (cached && cached !== refused)
          return { uid: user.uid, token: cached }
        return { uid: user.uid, token: await user.getIdToken(true) }
      } catch {
        return null
      }
    }).finally(() => {
      forcedRefreshes.delete(refused)
    })
    forcedRefreshes.set(refused, pending)
  }
  return pending.then((minted) =>
    minted && minted.uid === named ? minted.token : null
  )
}

async function isLapsedTokenReply(
  res: Response,
  sent: Record<string, string>
): Promise<boolean> {
  if (res.status !== 401 || !sent['x-authorization']) return false
  const code = await res
    .clone()
    .json()
    .then((b: { code?: unknown }) => b?.code)
    .catch((error: unknown) => {
      // A Stop pressed mid-read is the caller's cancel, not a reply to judge.
      if ((error as { name?: string } | null)?.name === 'AbortError')
        throw error
      return undefined
    })
  return code === ID_TOKEN_EXPIRED_CODE
}

/** Releases a 401 that is being replaced by a retry (only its clone was read). */
function release(res: Response): void {
  res.body?.cancel().catch(() => {})
}

/**
 * The token to send for `user`: the current one when `user` is the signed-in
 * user, else `user.accessToken` (the page-load token) as it was. Empty string
 * when signed out. For requests that carry no `x-user-id` to match against.
 *
 * ⛔ Use this, not `authUser.accessToken`: most api routes do not refuse an
 * expired token, they quietly treat the sender as anonymous. For the brain chat
 * stream use `fetchWithFreshIdToken`, which handles its 401.
 */
export async function idTokenForSend(
  user: { uid?: string; accessToken?: string } | null | undefined
): Promise<string> {
  const pageLoad = user?.accessToken ?? ''
  if (!user?.uid || user.uid !== auth.currentUser?.uid) return pageLoad
  return (await freshIdTokenForSend()) ?? pageLoad
}
